// Конфигурация API
// Без VITE_API_BASE_URL запросы идут на относительный /api, который Vite в dev проксирует на сервер (:10000)
// На рабочем сайте API живёт на поддомене api.croupierassistant.app: сессия лежит в HttpOnly-куке на общем домене сайта.
// В разработке и на других адресах работает прежнее правило: VITE_API_BASE_URL или относительный /api (Vite проксирует его на сервер :10000)
// Приложение — app.croupierassistant.app; на корне (croupierassistant.app) с октября 2026 лендинги и правила,
// старые адреса приложения оттуда переадресуются сюда (public/move.js)
const PRODUCTION_HOSTS = ['app.croupierassistant.app', 'croupierassistant.app', 'www.croupierassistant.app'];
const onProductionSite = typeof window !== 'undefined' && PRODUCTION_HOSTS.includes(window.location.hostname);
// Сайт с лендингами и правилами; в разработке они лежат по тем же путям рядом с приложением
export const SITE_URL = onProductionSite ? 'https://croupierassistant.app' : '';
export const API_BASE_URL = onProductionSite ? 'https://api.croupierassistant.app/api' : (import.meta.env?.VITE_API_BASE_URL as string) || '/api';
// Кука сессии доступна только при работе через поддомен сайта
const COOKIE_SESSION = onProductionSite || /^https?:\/\/[^/]*\.?croupierassistant\.app\//.test(API_BASE_URL);

export const API_ENDPOINTS = {
  auth: {
    signup: `${API_BASE_URL}/auth/signup`,
    signin: `${API_BASE_URL}/auth/signin`,
    me: `${API_BASE_URL}/auth/me`,
    logout: `${API_BASE_URL}/auth/logout`,
    deleteAccount: `${API_BASE_URL}/auth/delete-account`,
    verifyEmail: `${API_BASE_URL}/auth/verify-email`,
    resendVerification: `${API_BASE_URL}/auth/resend-verification`,
    forgotPassword: `${API_BASE_URL}/auth/forgot-password`,
    resetPassword: `${API_BASE_URL}/auth/reset-password`,
    changePassword: `${API_BASE_URL}/auth/change-password`,
  },
    games: {
      testStart: `${API_BASE_URL}/games/test-start`,
      testAbandon: `${API_BASE_URL}/games/test-abandon`,
      testResults: `${API_BASE_URL}/games/test-results`,
      ratings: (modeId: string, limit?: number) =>
        `${API_BASE_URL}/games/ratings/${modeId}${limit ? `?limit=${limit}` : ''}`,
      progress: (gameId: string) => `${API_BASE_URL}/games/progress/${gameId}`,
      userRating: (modeId: string) =>
        `${API_BASE_URL}/games/ratings/${modeId}/user?soft=1`, // без результата — 200 с пустым ответом, не 404
      friendRatings: (modeId: string) => `${API_BASE_URL}/games/ratings/${modeId}/friends`,
      statistics: `${API_BASE_URL}/games/statistics`,
    },
  config: {
    games: `${API_BASE_URL}/config/games`,
    game: (gameId: string) => `${API_BASE_URL}/config/games/${gameId}`,
    gameModes: (gameId: string) => `${API_BASE_URL}/config/games/${gameId}/modes`,
    gameMode: (gameId: string, modeId: string) => `${API_BASE_URL}/config/games/${gameId}/modes/${modeId}`,
    gameData: (gameId: string) => `${API_BASE_URL}/config/games/${gameId}/data`,
  },
};

// ── Сессия ──
// Вход хранится в HttpOnly-куке: скрипты страницы её не видят. В localStorage остаётся только пометка «был вход» (hint),
// чтобы гость не делал лишний запрос на проверку входа. Токен прежних версий (authToken) дослуживает до первого запроса:
// сервер в ответ на него выдаёт куку, после чего токен из браузера удаляется.
const HINT_KEY = 'ca_session_hint';
const LEGACY_TOKEN_KEY = 'authToken';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // хранилище недоступно: гость просто не запомнится
  }
}

// Токен прежней версии (если ещё остался в браузере)
export function getAuthToken(): string | null {
  return readStorage(LEGACY_TOKEN_KEY);
}

// Пометка «есть вход», которую сервер ставит рядом с кукой сессии на весь домен: по ней приложение на новом адресе
// узнаёт о входе, сделанном на старом (там, где осталась пометка в памяти браузера)
function signedInCookie(): boolean {
  return typeof document !== 'undefined' && document.cookie.split(';').some((part) => part.trim() === 'ca_signed_in=1');
}

// Есть ли смысл проверять вход на сервере
export function hasSession(): boolean {
  return readStorage(HINT_KEY) === '1' || readStorage(LEGACY_TOKEN_KEY) !== null || signedInCookie();
}

// После входа или регистрации: сервер уже поставил куку, запоминаем только пометку. Токен из ответа не сохраняем
export function markSession(): void {
  writeStorage(HINT_KEY, '1');
  writeStorage(LEGACY_TOKEN_KEY, null);
}

export function setAuthToken(_token?: string): void {
  markSession();
}

// Выход и истёкшая сессия
export function removeAuthToken(): void {
  writeStorage(HINT_KEY, null);
  writeStorage(LEGACY_TOKEN_KEY, null);
}

// Заголовок для запросов, которые идут мимо apiRequest (скачивание файла, keepalive при закрытии вкладки)
export function authHeaders(): Record<string, string> {
  const legacy = getAuthToken();
  return legacy ? { Authorization: `Bearer ${legacy}` } : {};
}

// Базовая функция для выполнения запросов
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const legacy = getAuthToken();
  // Content-Type нужен только запросам с телом: без него запрос к API «простой», и браузер не делает лишний предварительный OPTIONS
  const headers: Record<string, string> = {
    ...(options.body !== undefined && options.body !== null ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };

  // Прежний токен отправляем, пока сервер не выдал куку
  if (legacy) {
    headers['Authorization'] = `Bearer ${legacy}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    const errorMessage = error.error || `HTTP error! status: ${response.status}`;
    const errorWithCode = new Error(errorMessage) as Error & { code?: string };
    if (error.code) {
      errorWithCode.code = error.code;
    }
    throw errorWithCode;
  }

  // Сервер принял прежний токен и выдал куку: токен из браузера больше не нужен
  if (legacy && COOKIE_SESSION) markSession();

  return response.json();
}
